import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";
import { formatJMD } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Countdown } from "@/components/Countdown";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";

export default function CartPage() {
  const { items, updateQty, removeItem, subtotal, lineKey, count } = useCart();
  const navigate = useNavigate();
  const [promo, setPromo] = useState(null);
  useEffect(() => { api.get("/promo").then((r) => setPromo(r.data)).catch(() => {}); }, []);
  const saleActive = !!promo?.active;
  const discount = saleActive ? Math.round(subtotal * promo.sale_percent / 100) : 0;
  const freeShip = saleActive && count >= promo.free_ship_min_items;

  if (items.length === 0) {
    return (
      <div data-testid="cart-page" className="pt-32 pb-24 text-center max-w-md mx-auto px-4">
        <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
        <h1 className="font-display text-4xl tracking-tight">YUH BAG EMPTY</h1>
        <p className="text-zinc-400 text-sm mt-2">Nuh worry, plenty vibes waiting inna di shop.</p>
        <Link to="/shop">
          <Button className="mt-6 px-8 py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none">
            Shop Now
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
      <h1 className="font-display text-5xl tracking-tight mb-8">YUH BAG</h1>
      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-4">
          {items.map((i) => {
            const key = lineKey(i);
            return (
              <div key={key} className="flex gap-4 border border-white/10 bg-card p-3">
                <img src={i.image} alt={i.title} className="w-20 h-24 object-cover bg-zinc-900" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold uppercase text-sm">{i.title}</h3>
                  <p className="font-mono text-[10px] text-zinc-500 uppercase mt-1">{i.size} · {i.color}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={() => updateQty(key, i.qty - 1)} className="w-7 h-7 border border-border flex items-center justify-center hover:border-jamaica-gold"><Minus className="w-3 h-3" /></button>
                    <span className="font-mono text-xs w-6 text-center">{i.qty}</span>
                    <button onClick={() => updateQty(key, i.qty + 1)} className="w-7 h-7 border border-border flex items-center justify-center hover:border-jamaica-gold"><Plus className="w-3 h-3" /></button>
                    <button onClick={() => removeItem(key)} className="ml-auto text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="font-mono text-sm text-jamaica-gold">{formatJMD(i.price_cents * i.qty)}</div>
              </div>
            );
          })}
        </div>

        <div className="border border-white/10 bg-card p-5 h-fit lg:sticky lg:top-24">
          <h2 className="font-heading font-bold uppercase text-sm mb-4">Order Summary</h2>
          <div className="flex justify-between font-mono text-sm mb-2">
            <span className="text-zinc-400 uppercase text-xs tracking-widest">Subtotal</span>
            <span data-testid="cart-page-subtotal">{formatJMD(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between font-mono text-sm mb-2 text-jamaica-green">
              <span className="uppercase text-xs tracking-widest">Sale {promo.sale_percent}% Off</span>
              <span data-testid="cart-page-discount">−{formatJMD(discount)}</span>
            </div>
          )}
          {saleActive ? (
            <p className="text-[11px] mb-2" data-testid="cart-free-ship-note">
              {freeShip
                ? <span className="text-jamaica-green">Free Knutsford/ZipMail shipping unlocked!</span>
                : <span className="text-jamaica-gold">Add {promo.free_ship_min_items - count} more item(s) for FREE shipping.</span>}
            </p>
          ) : (
            <p className="text-[11px] text-zinc-500 mb-4">Delivery calculated at checkout.</p>
          )}
          {saleActive && promo.ends_at && <div className="mb-4"><Countdown endsAt={promo.ends_at} /></div>}
          <Button
            data-testid="cart-checkout-btn"
            onClick={() => navigate("/checkout")}
            className="w-full py-6 bg-jamaica-gold hover:bg-yellow-300 text-black font-bold uppercase tracking-widest rounded-none"
          >
            Proceed To Checkout
          </Button>
        </div>
      </div>
    </div>
  );
}
